import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchableSelect from './SearchableSelect';
import comunicacionesEmailsService from '../../services/ComunicacionesEmailsService';
import './AdminComunicacionesModal.css';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const splitEmails = (value) =>
  String(value || '')
    .split(/[\n,;]+/g)
    .map((item) => normalizeEmail(item))
    .filter(Boolean);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));

const formatCollegeName = (colegio) =>
  colegio?.nombre || colegio?.Nombre || colegio?.name || 'Colegio';

const formatProfesorName = (profesor) => {
  const nombre = String(profesor?.nombre || profesor?.Nombre || '').trim();
  const apellido = String(profesor?.apellido || profesor?.Apellido || '').trim();
  const displayName = String(profesor?.displayName || profesor?.DisplayName || '').trim();
  return [nombre, apellido].filter(Boolean).join(' ') || displayName || profesor?.email || profesor?.Email || 'Profesor';
};

const getProfesorId = (profesor) => String(profesor?.id || profesor?.Id || '');
const getProfesorEmail = (profesor) => normalizeEmail(profesor?.email || profesor?.Email || '');
const getProfesorColegios = (profesor) => {
  const colegios = profesor?.colegios || profesor?.Colegios || [];
  return Array.isArray(colegios) ? colegios : [];
};

const EditorToolbar = ({ editorRef, onColorChange }) => {
  const applyCommand = (command, value) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
  };

  const handleCreateLink = () => {
    if (!editorRef.current) return;
    const url = window.prompt('URL del enlace');
    if (!url) return;
    applyCommand('createLink', url);
  };

  return (
    <div className="comms-editor-toolbar">
      <button type="button" onClick={() => applyCommand('bold')} title="Negrita">B</button>
      <button type="button" onClick={() => applyCommand('italic')} title="Itálica">I</button>
      <button type="button" onClick={() => applyCommand('underline')} title="Subrayado">U</button>
      <button type="button" onClick={() => applyCommand('formatBlock', 'H2')} title="Título H2">H2</button>
      <button type="button" onClick={() => applyCommand('formatBlock', 'H3')} title="Título H3">H3</button>
      <button type="button" onClick={() => applyCommand('insertUnorderedList')} title="Lista">• Lista</button>
      <button type="button" onClick={() => applyCommand('insertOrderedList')} title="Lista numerada">1. Lista</button>
      <button type="button" onClick={() => applyCommand('justifyLeft')} title="Alinear izquierda">
        <i className="fas fa-align-left" />
      </button>
      <button type="button" onClick={() => applyCommand('justifyCenter')} title="Centrar">
        <i className="fas fa-align-center" />
      </button>
      <button type="button" onClick={() => applyCommand('justifyRight')} title="Alinear derecha">
        <i className="fas fa-align-right" />
      </button>
      <button type="button" onClick={handleCreateLink} title="Insertar enlace">
        <i className="fas fa-link" />
      </button>
      <button type="button" onClick={() => applyCommand('removeFormat')} title="Quitar formato">
        <i className="fas fa-eraser" />
      </button>
      <label className="comms-editor-color">
        <span>Texto</span>
        <input type="color" defaultValue="#1e293b" onChange={(event) => {
          onColorChange(event.target.value);
          applyCommand('foreColor', event.target.value);
        }} />
      </label>
    </div>
  );
};

const RecipientsModal = ({
  open,
  onClose,
  scope,
  selectedCollegeName,
  professors,
  loading,
  error,
  selectedProfessorIds,
  setSelectedProfessorIds,
  manualRecipients,
  setManualRecipients
}) => {
  const [search, setSearch] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) {
      setSearch('');
      setManualInput('');
      setLocalError('');
    }
  }, [open]);

  const filteredProfessors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return professors;
    return professors.filter((profesor) => {
      const text = [
        formatProfesorName(profesor),
        getProfesorEmail(profesor),
        ...getProfesorColegios(profesor).map((colegio) => colegio?.nombre || colegio?.Nombre || '')
      ].join(' ').toLowerCase();
      return text.includes(term);
    });
  }, [professors, search]);

  const selectedSet = useMemo(() => new Set(selectedProfessorIds.map(String)), [selectedProfessorIds]);

  const toggleProfessor = (profesorId) => {
    const normalized = String(profesorId);
    setSelectedProfessorIds((prev) => (
      prev.includes(normalized)
        ? prev.filter((item) => item !== normalized)
        : [...prev, normalized]
    ));
  };

  const selectVisible = () => {
    const visibleIds = filteredProfessors.map((item) => getProfesorId(item)).filter(Boolean);
    setSelectedProfessorIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const selectAllProfessors = () => {
    const allIds = professors.map((item) => getProfesorId(item)).filter(Boolean);
    setSelectedProfessorIds((prev) => Array.from(new Set([...prev, ...allIds])));
  };

  const clearProfesores = () => {
    setSelectedProfessorIds([]);
  };

  const addManualRecipients = () => {
    const parsed = splitEmails(manualInput);
    if (parsed.length === 0) {
      setLocalError('Escribe al menos un email válido para agregar.');
      return;
    }

    const invalid = parsed.filter((email) => !isValidEmail(email));
    if (invalid.length > 0) {
      setLocalError(`Hay emails inválidos: ${invalid.slice(0, 5).join(', ')}`);
      return;
    }

    setManualRecipients((prev) => Array.from(new Set([...prev, ...parsed])));
    setManualInput('');
    setLocalError('');
  };

  const removeManualRecipient = (email) => {
    setManualRecipients((prev) => prev.filter((item) => item !== email));
  };

  if (!open) return null;

  return (
    <div className="comms-recipient-overlay" onClick={onClose}>
      <div className="comms-recipient-modal" onClick={(event) => event.stopPropagation()}>
        <div className="comms-recipient-header">
          <div>
            <p className="comms-modal-eyebrow">Destinatarios</p>
            <h3>{scope === 'general' ? 'Profesores de todos los colegios' : `Profesores de ${selectedCollegeName}`}</h3>
          </div>
          <button type="button" className="comms-modal-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="comms-recipient-grid">
          <section className="comms-recipient-panel">
            <div className="comms-panel-head">
              <div>
                <h4>Agregar emails manuales</h4>
                <p>Podés pegar uno o varios emails separados por coma, punto y coma o salto de línea.</p>
              </div>
            </div>
            <textarea
              className="comms-recipient-textarea"
              value={manualInput}
              onChange={(event) => setManualInput(event.target.value)}
              placeholder="ejemplo@colegio.edu.ar&#10;otro@colegio.edu.ar"
            />
            <div className="comms-recipient-actions">
              <button type="button" className="btn btn-primary" onClick={addManualRecipients}>
                <i className="fas fa-plus" />
                <span>Agregar emails</span>
              </button>
            </div>

            {manualRecipients.length > 0 && (
              <div className="comms-chip-list">
                {manualRecipients.map((email) => (
                  <button
                    key={email}
                    type="button"
                    className="comms-chip"
                    onClick={() => removeManualRecipient(email)}
                    title={`Quitar ${email}`}
                  >
                    <span>{email}</span>
                    <i className="fas fa-times" />
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="comms-recipient-panel">
            <div className="comms-panel-head">
              <div>
                <h4>Agenda de profesores</h4>
                <p>Seleccioná profesores para agregarlos como destinatarios principales.</p>
              </div>
              <div className="comms-panel-head-actions">
                <button type="button" className="btn btn-secondary" onClick={selectAllProfessors} disabled={loading || professors.length === 0}>
                  Seleccionar todos
                </button>
                <button type="button" className="btn btn-secondary" onClick={selectVisible} disabled={loading || filteredProfessors.length === 0}>
                  Seleccionar visibles
                </button>
                <button type="button" className="btn btn-secondary" onClick={clearProfesores} disabled={selectedProfessorIds.length === 0}>
                  Limpiar
                </button>
              </div>
            </div>

            <div className="comms-search-box">
              <i className="fas fa-search" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, email o colegio..."
              />
            </div>

            <div className="comms-professor-list">
              {loading && (
                <div className="comms-professor-state">
                  <i className="fas fa-spinner fa-spin" />
                  <span>Cargando agenda...</span>
                </div>
              )}

              {!loading && error && (
                <div className="comms-professor-state error">{error}</div>
              )}

              {!loading && !error && filteredProfessors.length === 0 && (
                <div className="comms-professor-state">No hay profesores para mostrar.</div>
              )}

              {!loading && !error && filteredProfessors.map((profesor) => {
                const profesorId = getProfesorId(profesor);
                const isSelected = selectedSet.has(profesorId);
                const colegios = getProfesorColegios(profesor);
                return (
                  <button
                    key={profesorId}
                    type="button"
                    className={`comms-professor-row${isSelected ? ' selected' : ''}`}
                    onClick={() => toggleProfessor(profesorId)}
                  >
                    <span className="comms-professor-check">
                      <i className={`fas fa-${isSelected ? 'check-circle' : 'circle'}`} />
                    </span>
                    <span className="comms-professor-main">
                      <strong>{formatProfesorName(profesor)}</strong>
                      <span>{getProfesorEmail(profesor)}</span>
                      {colegios.length > 0 && (
                        <span className="comms-professor-badges">
                          {colegios.map((colegio) => (
                            <span key={colegio?.id || colegio?.Id || colegio?.nombre || colegio?.Nombre} className="comms-mini-badge">
                              {colegio?.nombre || colegio?.Nombre || 'Colegio'}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        {(localError || error) && (
          <div className="comms-inline-error">{localError || error}</div>
        )}

        <div className="comms-recipient-footer">
          <p>
            Destinatarios seleccionados: <strong>{manualRecipients.length}</strong> manuales y <strong>{selectedProfessorIds.length}</strong> profesores.
          </p>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Listo
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminComunicacionesModal = ({ open, onClose, colegios = [], onSuccess }) => {
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState('');
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [templateHtml, setTemplateHtml] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [scope, setScope] = useState('general');
  const [selectedColegioId, setSelectedColegioId] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [ccoInput, setCcoInput] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [professorsCache, setProfessorsCache] = useState({});
  const [professorsLoading, setProfessorsLoading] = useState(false);
  const [professorsError, setProfessorsError] = useState('');
  const [selectedProfessorIds, setSelectedProfessorIds] = useState([]);
  const [manualRecipients, setManualRecipients] = useState([]);
  const editorRef = useRef(null);

  const selectedCollege = useMemo(
    () => colegios.find((item) => String(item?.id || item?.Id || '') === String(selectedColegioId)),
    [colegios, selectedColegioId]
  );

  const collegeOptions = useMemo(() => (
    colegios.map((colegio) => ({
      value: String(colegio?.id || colegio?.Id || ''),
      label: formatCollegeName(colegio),
      secondary: String(colegio?.descripcion || colegio?.Descripcion || colegio?.detalle || colegio?.Detalle || '')
    }))
  ), [colegios]);

  const activeProfessorsKey = scope === 'colegio' ? String(selectedColegioId || 'colegio-vacio') : 'general';
  const activeProfessors = useMemo(
    () => professorsCache[activeProfessorsKey] || [],
    [professorsCache, activeProfessorsKey]
  );

  const ccList = useMemo(() => splitEmails(ccInput).filter(isValidEmail), [ccInput]);
  const ccoList = useMemo(() => splitEmails(ccoInput).filter(isValidEmail), [ccoInput]);

  const recipientSummary = useMemo(() => {
    const professorsMap = new Map(activeProfessors.map((item) => [getProfesorId(item), item]));
    const profesores = selectedProfessorIds.map((id) => professorsMap.get(String(id))).filter(Boolean);
    return {
      total: manualRecipients.length + profesores.length,
      profesores
    };
  }, [activeProfessors, manualRecipients.length, selectedProfessorIds]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const loadConfig = async () => {
      setConfigLoading(true);
      setConfigError('');
      setSubmitError('');
      try {
        const response = await comunicacionesEmailsService.getConfig();
        if (cancelled) return;
        const cuentas = response?.cuentasRemitente || response?.CuentasRemitente || [];
        const html = response?.htmlTemplateBase || response?.HtmlTemplateBase || '';
        setSenderAccounts(Array.isArray(cuentas) ? cuentas : []);
        setTemplateHtml(html);
        setHtmlBody((prev) => prev || html);
        const defaultAccount = (Array.isArray(cuentas) ? cuentas : []).find((item) => item.default || item.Default) || (Array.isArray(cuentas) ? cuentas[0] : null);
        setFromEmail((prev) => prev || defaultAccount?.email || defaultAccount?.Email || '');
      } catch (error) {
        if (!cancelled) {
          setConfigError(error.message || 'No pudimos cargar la configuración de comunicaciones.');
        }
      } finally {
        if (!cancelled) {
          setConfigLoading(false);
        }
      }
    };

    loadConfig();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== htmlBody) {
      editorRef.current.innerHTML = htmlBody || '';
    }
  }, [htmlBody]);

  useEffect(() => {
    setSelectedProfessorIds([]);
    setProfessorsError('');
    if (scope === 'general') {
      return;
    }
    if (!selectedColegioId) {
      setRecipientsModalOpen(false);
    }
  }, [scope, selectedColegioId]);

  const ensureProfesoresLoaded = async () => {
    if (scope === 'colegio' && !selectedColegioId) {
      setProfessorsError('Selecciona un colegio antes de abrir la agenda de profesores.');
      return;
    }

    if (professorsCache[activeProfessorsKey]) {
      setProfessorsError('');
      return;
    }

    setProfessorsLoading(true);
    setProfessorsError('');
    try {
      const response = await comunicacionesEmailsService.getProfesores(scope === 'colegio' ? selectedColegioId : null);
      const profesores = response?.profesores || response?.Profesores || [];
      setProfessorsCache((prev) => ({
        ...prev,
        [activeProfessorsKey]: Array.isArray(profesores) ? profesores : []
      }));
    } catch (error) {
      setProfessorsError(error.message || 'No pudimos cargar la agenda de profesores.');
    } finally {
      setProfessorsLoading(false);
    }
  };

  const openRecipientsModal = async () => {
    await ensureProfesoresLoaded();
    setRecipientsModalOpen(true);
  };

  const handleClose = () => {
    if (submitLoading) return;
    setRecipientsModalOpen(false);
    onClose?.();
  };

  const handleResetTemplate = () => {
    setHtmlBody(templateHtml);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = templateHtml;
      }
    }, 0);
  };

  const handleSubmit = async () => {
    const currentHtml = editorRef.current?.innerHTML || htmlBody;
    const invalidCc = splitEmails(ccInput).filter((email) => !isValidEmail(email));
    const invalidCco = splitEmails(ccoInput).filter((email) => !isValidEmail(email));

    if (scope === 'colegio' && !selectedColegioId) {
      setSubmitError('Debes seleccionar un colegio para una comunicación particular.');
      return;
    }

    if (!fromEmail) {
      setSubmitError('Debes seleccionar una cuenta remitente.');
      return;
    }

    if (!subject.trim()) {
      setSubmitError('Debes indicar un asunto.');
      return;
    }

    if (!currentHtml || !currentHtml.trim()) {
      setSubmitError('Debes completar el contenido del email.');
      return;
    }

    if (manualRecipients.length === 0 && selectedProfessorIds.length === 0) {
      setSubmitError('Debes seleccionar al menos un destinatario.');
      return;
    }

    if (invalidCc.length > 0) {
      setSubmitError(`Hay emails inválidos en CC: ${invalidCc.slice(0, 5).join(', ')}`);
      return;
    }

    if (invalidCco.length > 0) {
      setSubmitError(`Hay emails inválidos en CCO: ${invalidCco.slice(0, 5).join(', ')}`);
      return;
    }

    const sender = senderAccounts.find((item) => normalizeEmail(item?.email || item?.Email) === normalizeEmail(fromEmail));
    setSubmitLoading(true);
    setSubmitError('');

    try {
      const response = await comunicacionesEmailsService.createProceso({
        esGeneral: scope === 'general',
        idColegio: scope === 'colegio' ? selectedColegioId : null,
        fromEmail,
        fromName: sender?.nombre || sender?.Nombre || '',
        profesorIds: selectedProfessorIds,
        destinatariosManuales: manualRecipients,
        cc: ccList,
        cco: ccoList,
        subject: subject.trim(),
        htmlBody: currentHtml
      });

      onSuccess?.({
        message: response?.mensaje || response?.Mensaje || 'La comunicación fue registrada correctamente.',
        processId: response?.procesoId || response?.ProcesoId || null,
        total: response?.cantidadDeRegistros ?? response?.CantidadDeRegistros ?? recipientSummary.total
      });
      handleClose();
    } catch (error) {
      setSubmitError(error.message || 'No pudimos crear la comunicación.');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="comms-modal-overlay" onClick={handleClose}>
        <div className="comms-modal" onClick={(event) => event.stopPropagation()}>
          <div className="comms-modal-header">
            <div>
              <p className="comms-modal-eyebrow">Comunicaciones</p>
              <h2>Envio institucional de emails</h2>
              <p>Disponible solo para administrador general. El envío se registra como proceso asíncrono y guarda estado por destinatario.</p>
            </div>
            <button type="button" className="comms-modal-close" onClick={handleClose}>
              <i className="fas fa-times" />
            </button>
          </div>

          <div className="comms-modal-body">
            {(configError || submitError) && (
              <div className="comms-alert error">
                <i className="fas fa-triangle-exclamation" />
                <span>{configError || submitError}</span>
              </div>
            )}

            <section className="comms-section">
              <div className="comms-section-title">
                <span className="comms-step">1</span>
                <div>
                  <h3>Alcance</h3>
                  <p>Define si la comunicación aplica a todos los colegios o a uno puntual.</p>
                </div>
              </div>

              <div className="comms-scope-grid">
                <button
                  type="button"
                  className={`comms-scope-card${scope === 'general' ? ' active' : ''}`}
                  onClick={() => setScope('general')}
                >
                  <i className="fas fa-globe-americas" />
                  <strong>General</strong>
                  <span>Agenda de profesores de toda la app.</span>
                </button>
                <button
                  type="button"
                  className={`comms-scope-card${scope === 'colegio' ? ' active' : ''}`}
                  onClick={() => setScope('colegio')}
                >
                  <i className="fas fa-school" />
                  <strong>Por colegio</strong>
                  <span>Se limita a la agenda de un colegio puntual.</span>
                </button>
              </div>

              {scope === 'colegio' && (
                <div className="comms-field">
                  <label>Colegio objetivo</label>
                  <SearchableSelect
                    value={selectedColegioId}
                    onChange={setSelectedColegioId}
                    options={collegeOptions}
                    placeholder="Buscar colegio..."
                    emptyText="No hay colegios disponibles."
                    noResultsText="No encontramos colegios con ese criterio."
                  />
                </div>
              )}
            </section>

            <section className="comms-section">
              <div className="comms-section-title">
                <span className="comms-step">2</span>
                <div>
                  <h3>Cuenta remitente</h3>
                  <p>Selecciona la cuenta configurada desde la que se enviará el email.</p>
                </div>
              </div>

              <div className="comms-sender-grid">
                {configLoading && (
                  <div className="comms-loading-card">
                    <i className="fas fa-spinner fa-spin" />
                    <span>Cargando cuentas configuradas...</span>
                  </div>
                )}

                {!configLoading && senderAccounts.map((account) => {
                  const email = account?.email || account?.Email || '';
                  const name = account?.nombre || account?.Nombre || email;
                  const isDefault = account?.default || account?.Default;
                  return (
                    <button
                      key={email}
                      type="button"
                      className={`comms-sender-card${normalizeEmail(fromEmail) === normalizeEmail(email) ? ' active' : ''}`}
                      onClick={() => setFromEmail(email)}
                    >
                      <span className="comms-sender-name">{name}</span>
                      <span className="comms-sender-email">{email}</span>
                      {isDefault && <span className="comms-mini-badge accent">Predeterminada</span>}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="comms-section">
              <div className="comms-section-title">
                <span className="comms-step">3</span>
                <div>
                  <h3>Destinatarios</h3>
                  <p>Agrega emails manuales y/o selecciona profesores desde la agenda correspondiente.</p>
                </div>
              </div>

              <div className="comms-recipient-summary">
                <div className="comms-recipient-stat">
                  <strong>{manualRecipients.length}</strong>
                  <span>emails manuales</span>
                </div>
                <div className="comms-recipient-stat">
                  <strong>{selectedProfessorIds.length}</strong>
                  <span>profesores</span>
                </div>
                <div className="comms-recipient-stat">
                  <strong>{recipientSummary.total}</strong>
                  <span>destinatarios únicos</span>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openRecipientsModal}
                  disabled={scope === 'colegio' && !selectedColegioId}
                >
                  <i className="fas fa-address-book" />
                  <span>Para</span>
                </button>
              </div>

              {recipientSummary.profesores.length > 0 && (
                <div className="comms-chip-list compact">
                  {recipientSummary.profesores.slice(0, 6).map((profesor) => (
                    <span key={getProfesorId(profesor)} className="comms-static-chip">
                      {formatProfesorName(profesor)}
                    </span>
                  ))}
                  {recipientSummary.profesores.length > 6 && (
                    <span className="comms-static-chip muted">+{recipientSummary.profesores.length - 6} más</span>
                  )}
                </div>
              )}
            </section>

            <section className="comms-section">
              <div className="comms-section-title">
                <span className="comms-step">4</span>
                <div>
                  <h3>Contenido del email</h3>
                  <p>El editor arranca con la plantilla visual de recuperación de contraseña, adaptada para comunicaciones institucionales.</p>
                </div>
              </div>

              <div className="comms-form-grid">
                <div className="comms-field">
                  <label>Asunto</label>
                  <input
                    type="text"
                    className="form-control"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    placeholder="Ej. Recordatorio de reunión con familias"
                  />
                </div>

                <div className="comms-field">
                  <label>CC</label>
                  <textarea
                    className="comms-inline-textarea"
                    value={ccInput}
                    onChange={(event) => setCcInput(event.target.value)}
                    placeholder="Emails separados por coma, punto y coma o salto de línea"
                  />
                  {ccList.length > 0 && <small>{ccList.length} emails válidos en CC.</small>}
                </div>

                <div className="comms-field">
                  <label>CCO</label>
                  <textarea
                    className="comms-inline-textarea"
                    value={ccoInput}
                    onChange={(event) => setCcoInput(event.target.value)}
                    placeholder="Emails separados por coma, punto y coma o salto de línea"
                  />
                  {ccoList.length > 0 && <small>{ccoList.length} emails válidos en CCO.</small>}
                </div>
              </div>

              <div className="comms-editor-card">
                <div className="comms-editor-header">
                  <div>
                    <h4>Editor HTML</h4>
                    <p>Puedes ajustar tipografías, títulos, listas, colores y enlaces.</p>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={handleResetTemplate}>
                    Restaurar plantilla
                  </button>
                </div>

                <EditorToolbar editorRef={editorRef} onColorChange={() => {}} />

                <div
                  ref={editorRef}
                  className="comms-editor-surface"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(event) => setHtmlBody(event.currentTarget.innerHTML)}
                />
              </div>

              <div className="comms-preview-card">
                <div className="comms-preview-header">
                  <h4>Vista previa</h4>
                  <p>Esto es lo que verán los destinatarios.</p>
                </div>
                <div className="comms-preview-body" dangerouslySetInnerHTML={{ __html: htmlBody }} />
              </div>
            </section>
          </div>

          <div className="comms-modal-footer">
            <div className="comms-footer-note">
              <i className="fas fa-clock-rotate-left" />
              <span>El envío se registra como proceso asíncrono y guarda estado individual por cada destinatario.</span>
            </div>
            <div className="comms-footer-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitLoading}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitLoading || configLoading}>
                <i className={`fas ${submitLoading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
                <span>{submitLoading ? 'Enviando a la cola...' : 'Enviar comunicación'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <RecipientsModal
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
        scope={scope}
        selectedCollegeName={selectedCollege ? formatCollegeName(selectedCollege) : 'todos los colegios'}
        professors={activeProfessors}
        loading={professorsLoading}
        error={professorsError}
        selectedProfessorIds={selectedProfessorIds}
        setSelectedProfessorIds={setSelectedProfessorIds}
        manualRecipients={manualRecipients}
        setManualRecipients={setManualRecipients}
      />
    </>
  );
};

export default AdminComunicacionesModal;
